import { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { ActionList } from '@m/alchemy-ui/ActionList';
import { Dialog } from '@m/alchemy-ui/Dialog';
import { MoreMenuButton } from '@m/alchemy-ui/MoreMenuButton';
import { PopperBox } from '@m/alchemy-ui/PopperBox';
import { Select } from '@m/alchemy-ui/Select';
import { DeleteIcon } from '@m/alchemy-ui/icons/DeleteIcon';
import { AUTO_PANEL_SIZE, type PanelSize, WIDE_PANEL_SIZE } from '../common/panel-types';
import { PanelSizeMenuLabel, PanelSizeMenuSection } from '../CustomPage.styled';

const messages = defineMessages({
  panelSize: {
    id: 'editCustomPagePanelSize',
    defaultMessage: 'Panel size'
  },
  panelSizeAuto: {
    id: 'editCustomPagePanelSizeAuto',
    defaultMessage: 'Auto'
  },
  panelSizeWide: {
    id: 'editCustomPagePanelSizeWide',
    defaultMessage: 'Wide'
  },
  panelOptions: {
    id: 'editCustomPagePanelOptions',
    defaultMessage: 'Options for {panel}'
  },
  deletePanel: {
    id: 'editCustomPageDeletePanel',
    defaultMessage: 'Delete panel'
  },
  deletePanelConfirmation: {
    id: 'editCustomPageDeletePanelConfirmation',
    defaultMessage: 'Are you sure you want to delete this element?'
  }
});

interface PanelOptionsMenuProps {
  readonly panelName: string;
  readonly showSizeSelect: boolean;
  readonly size: PanelSize;
  readonly labelId: string;
  /** Omit both callbacks for an inert copy (drop preview): the delete dialog, which portals, never opens. */
  readonly onSizeChange?: (size: PanelSize) => void;
  readonly onDelete?: () => void;
}

export const PanelOptionsMenu = ({
  panelName,
  showSizeSelect,
  size,
  labelId,
  onSizeChange,
  onDelete
}: PanelOptionsMenuProps) => {
  const intl = useIntl();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const sizeOptions = useMemo(
    () => [
      { value: AUTO_PANEL_SIZE as string, label: intl.formatMessage(messages.panelSizeAuto) },
      { value: WIDE_PANEL_SIZE as string, label: intl.formatMessage(messages.panelSizeWide) }
    ],
    [intl]
  );

  const closeConfirm = useCallback(() => setConfirmOpen(false), []);

  const handleConfirmDelete = useCallback(() => {
    setConfirmOpen(false);
    onDelete?.();
  }, [onDelete]);

  const footerButtons = useMemo(
    () => [
      {
        label: intl.formatMessage({ id: 'cancel', defaultMessage: 'Cancel' }),
        onClick: closeConfirm,
        priority: 'tertiary' as const
      },
      {
        label: intl.formatMessage({ id: 'delete', defaultMessage: 'Delete' }),
        onClick: handleConfirmDelete,
        destructive: true
      }
    ],
    [intl, closeConfirm, handleConfirmDelete]
  );

  return (
    <>
      <MoreMenuButton
        aria-label={intl.formatMessage(messages.panelOptions, { panel: panelName })}
        placement="bottom-end"
        overlay={({ closeAndReturnFocus }) => (
          <PopperBox hasPadding={false} maxWidth={260}>
            <div>
              {showSizeSelect && (
                <PanelSizeMenuSection>
                  <PanelSizeMenuLabel id={labelId}>{intl.formatMessage(messages.panelSize)}</PanelSizeMenuLabel>
                  <Select
                    labelId={labelId}
                    isFullWidth
                    value={size}
                    options={sizeOptions}
                    onChange={(option) => {
                      onSizeChange?.(option.value as PanelSize);
                      closeAndReturnFocus();
                    }}
                  />
                </PanelSizeMenuSection>
              )}
              <ActionList
                actions={[
                  {
                    label: intl.formatMessage(messages.deletePanel),
                    icon: <DeleteIcon aria-hidden="true" />,
                    onClick: () => {
                      closeAndReturnFocus();
                      if (onDelete) {
                        setConfirmOpen(true);
                      }
                    }
                  }
                ]}
              />
            </div>
          </PopperBox>
        )}
      />
      <Dialog
        isOpen={confirmOpen}
        onClose={closeConfirm}
        headerContent={intl.formatMessage({ id: 'areYouSure', defaultMessage: 'Are you sure?' })}
        bodyContent={intl.formatMessage(messages.deletePanelConfirmation)}
        footerButtons={footerButtons}
      />
    </>
  );
};
